import { useEffect, useMemo, useState } from 'react';
import BumbleTabs from '@bumble/app/components/BumbleTabs';
import CreateModal from '@bumble/app/components/CreateModal';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import ManageFormFactor from '@bumble/app/components/ManageFormFactor';
import { Loading } from '@bumble/app/components/Loading';
import { Indicator } from '@bumble/app/components/Indicator';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import { NodeHealthTest } from '@bumble/app/components/NodeHealthTest';
import NodeMetrics from '@bumble/app/components/NodeMetrics';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import SplitPanes from '@bumble/app/components/SplitPanes';
import {
  ServiceNodeCreate,
  ServiceNodeTabAppService,
  ServiceNodeTabConnectedEndpoints,
  ServiceNodeTabDetails,
} from '@bumble/app/components/service-node';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import { Button, Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import RoomPreferencesOutlinedIcon from '@mui/icons-material/RoomPreferencesOutlined';

const columns: GridColDef[] = [
  {
    field: 'service_node_name',
    headerName: 'Node Name',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'service_node_id',
    headerName: 'Service Node Id',
    width: 200,
    align: 'left',
    disableColumnMenu: true,
  },
  {
    field: 'service_node_group_id',
    headerName: 'Service Node Group Id',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'admin_state',
    headerName: 'Admin State',
    width: 120,
    disableColumnMenu: true,
  },
  {
    field: 'registered_at',
    headerName: 'Registered at',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'op_state',
    headerName: 'Op State',
    width: 120,
    disableColumnMenu: true,
    renderCell: (params) => {
      return params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A';
    }
  },
  // {
  //  field: 'public_ip',
  //  headerName: 'Service Node IP Address',
  //  width: 200,
  //  disableColumnMenu: true,
  // },
  {
    field: 'form_factor',
    headerName: 'Form Factor',
    width: 120,
    disableColumnMenu: true,
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    minWidth: 150,
    headerAlign: 'right',
    align: 'right',
    disableColumnMenu: true,
    valueGetter: (value) => {
      if (value === null) return '';
      return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
];

const columnVisible = {
  service_node_name: true,
  service_node_id: true,
  service_node_group_id: true,
  registered: true,
  registered_at: true,
  admin_state: true,
  op_state: true,
  public_ip: true,
  created_at: true,
};

export const ServiceNodes = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const handleCreateModalOpen = () => {
    setOpenCreateModal(true);
  };

  const [openFormFactorModal, setOpenFormFactorModal] = useState(false);

  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);
  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
    } else {
      setSelectedItemId('');
    }
  }, [rowSelectionModel, selectedRowItem]);

  const api = useApi();
  const { isLoading, data, refetch } = useQuery(
    ['serviceNodes'],
    api.listServiceNodesApiV1ListServiceNodeGet.bind(null, {}),
    { enabled: true },
  );

  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showHealthDialog, setShowHealthDialog] = useState<boolean>(false);

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed Service Nodes.');
  };

  const removeServiceNode = useMutation(api.removeServiceNodeApiV1RemoveServiceNodePost, {
    onSuccess() {
      toast.success('Service Node removed.');
      handleDataRefetch();
    },
    onError: apiError,
  });

  const handleServiceNodeDelete = () => {
    removeServiceNode.mutate({
      service_node_id: selectedItemId,
    });
  };

  const handleFormFactor = () => {
    setOpenFormFactorModal(true);
  };

  const handleCreateModalClose = () => {
    setOpenCreateModal(false);
    handleDataRefetch();
  };

  const handleNodeHealthTest = () => {
    setShowHealthDialog(true);
  };

  const {
    isLoading: detailLoading,
    data: detailData,
    refetch: detailRefetch,
  } = useQuery(
    ['serviceNodeDetail'],
    api.getServiceNodeDetailApiV1GetServiceNodeDetailGet.bind(null, {
      service_node_id: selectedRowItem,
    }),
    { enabled: false },
  );

  useEffect(() => {
    if (selectedRowItem) {
      detailRefetch();
    }
  }, [detailRefetch, selectedRowItem]);

  return (
    <PageContent>
      <PageHeader
        pageName="Service Nodes"
        count={data?.data?.length || 0}
        actions={
          <Button
            variant="text"
            startIcon={<FolderOutlinedIcon fontSize="small" />}
            component={NavLink}
            to="/service-node-groups"
          >
            Manage Service Node Groups
          </Button>
        }
      />
      {isLoading ? (
        <Loading />
      ) : (
        <SplitPanes
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={
                    data?.data.map((item) => {
                      return {
                        ...item,
                        id: item.service_node_id,
                      };
                    }) || []
                  }
                  columns={columns}
                  handleSelectionChange={handleSelectionChange}
                  rowSelectionModel={rowSelectionModel}
                  itemsSelected={rowSelectionModel.length === 1}
                  newLabel="Create Service Node"
                  newOnClick={handleCreateModalOpen}
                  columnVisibilityModel={columnVisible}
                  // getRowId={(row: ServiceNode) => row.service_node_id}
                  refreshData={handleDataRefetch}
                  menuItems={[
                    {
                      label: 'Manage Form Factor',
                      icon: <RoomPreferencesOutlinedIcon color="info" fontSize="small" />,
                      onClick: handleFormFactor,
                    },
                    {
                      label: 'Test Node Health',
                      icon: <MonitorHeartOutlinedIcon color="info" fontSize="small" />,
                      onClick: handleNodeHealthTest,
                    },
                    {
                      label: 'Delete',
                      icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                      onClick: () => setShowConfirmDelete(true),
                      hasDivider: true,
                    },
                  ]}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  <BumbleTabs
                    tabs={[
                      {
                        el: (
                          <ServiceNodeTabDetails data={detailData?.data} loading={detailLoading} />
                        ),
                        label: 'Details',
                        id: 0,
                      },
                      {
                        el: (
                          <ServiceNodeTabAppService
                            data={detailData?.data}
                            loading={detailLoading}
                          />
                        ),
                        label: 'App Services',
                        id: 1,
                      },
                      {
                        el: (
                          <ServiceNodeTabConnectedEndpoints
                            data={detailData?.data}
                            loading={detailLoading}
                          />
                        ),
                        label: 'Connected Endpoints',
                        id: 2,
                      },
                      {
                        el: <NodeMetrics id={selectedRowItem} />,
                        label: 'Monitoring',
                        id: 3,
                      },
                    ]}
                  />
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      )}
      {openCreateModal && (
        <CreateModal
          open={openCreateModal}
          handleClose={handleCreateModalClose}
          title="Create Service Node"
        >
          <ServiceNodeCreate onCancel={handleCreateModalClose} />
        </CreateModal>
      )}
      {openFormFactorModal && selectedItemId && (
        <DialogStandard
          open={openFormFactorModal}
          handleClose={() => setOpenFormFactorModal(false)}
          title="Manage Form Factor"
        >
          <ManageFormFactor type="service" selectedNode={selectedItemId} onClose={() => setOpenFormFactorModal(false)} />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleServiceNodeDelete}
          title="Delete Service Node?"
          description={`Are you certain you wish to proceed with deleting ${selectedItemId}? Please note that this action is irreversible and will result in the permanent removal of the service node.`}
          confirmationPhrase="confirm"
        />
      )}
      {showHealthDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showHealthDialog}
          handleClose={() => setShowHealthDialog(false)}
          title="Node Health Check"
          size="md"
        >
          <NodeHealthTest nodeId={selectedItemId} />
        </DialogStandard>
      )}
    </PageContent>
  );
};
